// src/screens/Admin/RevenueAnalyticsScreen.js

import React from 'react';
import { useNavigate } from 'react-router-dom';

import './RevenueAnalyticsScreen.css';

import backIcon from '../../assets/backspace-icon.png';

const revenueExperiments = [
  {
    key: 'profile_banners',
    title: 'Profile Banners',
    description:
      'Track banner discovery, previews, trials, purchases and Pi revenue.',
    icon: '🖼️',
    status: 'live',
    route: '/admin/revenue-analytics/banners',
    metrics: [
      'Discovery',
      'Preview interest',
      'Trial conversion',
      'Revenue',
    ],
  },
  {
    key: 'post_backgrounds',
    title: 'Post Backgrounds',
    description:
      'Measure whether styled posts create enough desire to generate purchases.',
    icon: '🎨',
    status: 'coming_soon',
    route: '',
    metrics: [
      'Style views',
      'Preview usage',
      'Purchases',
      'Revenue',
    ],
  },
  {
    key: 'plans',
    title: 'Plans',
    description:
      'Understand plan discovery, comparison, upgrades, renewals and revenue.',
    icon: '💎',
    status: 'coming_soon',
    route: '',
    metrics: [
      'Plan views',
      'Upgrade intent',
      'Subscriptions',
      'Renewals',
    ],
  },
  {
    key: 'post_stakes',
    title: 'Post Stakes',
    description:
      'Track stake discovery, completed boosts and Pi generated from promoted posts.',
    icon: '🚀',
    status: 'coming_soon',
    route: '',
    metrics: [
      'Stake opens',
      'Completed stakes',
      'Average stake',
      'Revenue',
    ],
  },
  {
    key: 'tips',
    title: 'Tips',
    description:
      'Measure tipping intent, completed tips, repeat tippers and platform revenue.',
    icon: '🪙',
    status: 'coming_soon',
    route: '',
    metrics: [
      'Tip drawer opens',
      'Completed tips',
      'Repeat tippers',
      'Revenue',
    ],
  },
  {
    key: 'escrow',
    title: 'Escrow',
    description:
      'Understand escrow adoption, completed orders, commissions and repeat usage.',
    icon: '🤝',
    status: 'coming_soon',
    route: '',
    metrics: [
      'Escrow starts',
      'Completed orders',
      'Commission',
      'Repeat buyers',
    ],
  },
];

function ExperimentStatus({ status }) {
  const isLive = status === 'live';

  return (
    <span
      className={`revenue-hub-status ${
        isLive ? 'is-live' : 'is-coming-soon'
      }`}
    >
      {isLive ? 'Live' : 'Coming Soon'}
    </span>
  );
}

function RevenueAnalyticsScreen() {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin');
  };

  const handleExperimentClick = (experiment) => {
    if (experiment.status !== 'live' || !experiment.route) {
      return;
    }

    navigate(experiment.route);
  };

  return (
    <div className="revenue-hub-screen">
      <header className="revenue-hub-topbar">
        <button
          type="button"
          className="revenue-hub-back-btn"
          onClick={handleBack}
          aria-label="Back to admin dashboard"
        >
          <img
            src={backIcon}
            alt=""
          />
        </button>

        <div className="revenue-hub-topbar-title">
          <h1>Revenue Analytics</h1>
          <span>Revenue experiments and monetization performance</span>
        </div>

        <div className="revenue-hub-topbar-space" />
      </header>

      <main className="revenue-hub-scroll">
        <section className="revenue-hub-intro">
          <div className="revenue-hub-intro-copy">
            <span className="revenue-hub-eyebrow">
              Monetization experiments
            </span>

            <h2>
              Find what users value enough to pay for
            </h2>

            <p>
              Each tile represents one revenue stream. Open an active
              experiment to study discovery, conversion, purchases and
              real Pi revenue.
            </p>
          </div>

          <div className="revenue-hub-summary">
            <div>
              <span>Live experiments</span>
              <strong>
                {
                  revenueExperiments.filter(
                    (experiment) => experiment.status === 'live'
                  ).length
                }
              </strong>
            </div>

            <div>
              <span>Planned experiments</span>
              <strong>
                {
                  revenueExperiments.filter(
                    (experiment) =>
                      experiment.status === 'coming_soon'
                  ).length
                }
              </strong>
            </div>
          </div>
        </section>

        <section className="revenue-hub-section">
          <div className="revenue-hub-section-heading">
            <div>
              <span>Experiment library</span>
              <h3>Revenue Streams</h3>
            </div>

            <p>
              Open a live tile to view its detailed performance.
            </p>
          </div>

          <div className="revenue-hub-grid">
            {revenueExperiments.map((experiment) => {
              const isLive = experiment.status === 'live';

              return (
                <button
                  key={experiment.key}
                  type="button"
                  className={`revenue-hub-card ${
                    isLive ? 'is-live' : 'is-disabled'
                  }`}
                  onClick={() =>
                    handleExperimentClick(experiment)
                  }
                  disabled={!isLive}
                  aria-disabled={!isLive}
                >
                  <div className="revenue-hub-card-top">
                    <div className="revenue-hub-card-icon">
                      {experiment.icon}
                    </div>

                    <ExperimentStatus
                      status={experiment.status}
                    />
                  </div>

                  <div className="revenue-hub-card-copy">
                    <h4>{experiment.title}</h4>

                    <p>{experiment.description}</p>
                  </div>

                  <div className="revenue-hub-card-metrics">
                    {experiment.metrics.map((metric) => (
                      <span key={metric}>
                        {metric}
                      </span>
                    ))}
                  </div>

                  <div className="revenue-hub-card-footer">
                    <span>
                      {isLive
                        ? 'View analytics'
                        : 'Not available yet'}
                    </span>

                    {isLive && (
                      <strong aria-hidden="true">
                        →
                      </strong>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

export default RevenueAnalyticsScreen;
